/**
 * ProductHistoryTab — zentralisierter Änderungsverlauf.
 * Quelle: ProductChangeEventService via useChangeEvents (Text + Bild, alle Scopes).
 * Komplette Historie (kein from). WCAG 2.1 AA.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { History } from 'lucide-react'
import { useChangeEvents } from '@/hooks/useChangeEvents'
import { useProductChannelList } from '@/hooks/useProductChannelList'
import { useSearchParams } from 'react-router-dom'
import { Loading, EmptyState } from '@/components/v2/ui'
import { Select } from '@/components/v2/ui/Select'
import { EID } from '@/lib/element-ids.registry'
import { changeEventToRow, type HistoryRow } from './history/changeEventToRow'
import { HistoryEntryRow } from './history/HistoryEntryRow'

type Filter = 'all' | 'content' | 'assets'

const TYPES: Record<Filter, ('image' | 'text')[]> = {
  all: ['image', 'text'],
  content: ['text'],
  assets: ['image'],
}

const FILTERS: Filter[] = ['all', 'content', 'assets']

export function ProductHistoryTab({ productId }: { productId: string }) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<Filter>('all')
  const [channelId, setChannelId] = useState<string | null>(null)
  const [, setSearchParams] = useSearchParams()
  const channels = useProductChannelList(productId)

  const channelName = (id: string | null) =>
    id
      ? (channels.find((c) => c.id === id)?.name ?? t('productHistory.channel'))
      : t('productHistory.master')

  const { data, isLoading } = useChangeEvents(productId, { types: TYPES[filter], channelId: channelId ?? undefined })

  const rows: HistoryRow[] = useMemo(
    () =>
      (data ?? [])
        .map((e) => changeEventToRow(e, channelName))
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, channels],
  )

  const openEntry = (row: HistoryRow) =>
    setSearchParams(
      row.track === 'asset'
        ? { tab: 'assets', channel: row.channelId ?? '', slot: String(row.slot ?? '') }
        : { tab: 'content', channel: row.channelId ?? '', block: row.blockType ?? '' },
    )

  return (
    <div className="product-history-tab space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h5 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
            {t('productHistory.title')}
          </h5>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            {t('productHistory.description')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-44">
            <Select
              id={EID['product-history-channel-select']}
              options={[{ value: '__all__', label: t('performance.channel.all') }, ...channels.map((c) => ({ value: c.id, label: c.name }))]}
              value={channelId ?? '__all__'}
              onValueChange={(v) => setChannelId(v === '__all__' ? null : v)}
              aria-label={t('performance.channel.all')}
            />
          </div>
          <div className="flex gap-1 rounded-lg border border-neutral-200 dark:border-neutral-800 p-1">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                id={EID[`product-history-filter-${f}-btn` as keyof typeof EID]}
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 text-sm rounded ${
                  filter === f
                    ? 'bg-primary-600 text-white'
                    : 'text-neutral-700 dark:text-neutral-300'
                }`}
              >
                {t(`productHistory.filter.${f}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loading size="lg" label={t('productHistory.loading')} />
        </div>
      ) : rows.length > 0 ? (
        <div className="space-y-3">
          {rows.map((r) => (
            <HistoryEntryRow key={r.id} row={r} onOpen={openEntry} />
          ))}
        </div>
      ) : (
        <EmptyState icon={<History className="h-12 w-12" />} title={t('productHistory.empty')} />
      )}
    </div>
  )
}
